/**
 * useMapFold - whether THIS viewer's map is folded behind the log.
 *
 * The phase says whether the map starts folded (`engine/phase/layout`); the
 * viewer may open it, and that choice lasts until the phase changes. It is
 * per viewer and changes nothing about how an attack resolves - looking at
 * the map is not the same as positions counting.
 *
 * Whatever WANTS the map in view never folds it: Build and Deploy
 * (`mapWantsColumn`). The rails layout no longer does (2026-10-20): it kept
 * Narrative's map open on the rails only, so a GM and a player in the same
 * mode saw different screens; Stealth now shows the map everywhere instead.
 *
 * @param {import('../engine/phase/types').Phase | null} phase - the phase on this screen
 * @param {boolean} mapWanted - Build or Deploy open
 * @returns {{ canFold: boolean, folded: boolean, toggle: () => void }}
 */
import { useCallback, useState } from 'react';
import { layoutOf } from '../engine/phase/layout';

export default function useMapFold(phase, mapWanted) {
  // The phase the viewer opened the map in. Any other phase starts folded again, with no effect to reset it.
  const [openedIn, setOpenedIn] = useState(null);
  // Forgotten as soon as the phase moves on (state adjusted while rendering), so coming back to a
  // phase does not find the map open from the last time.
  if (openedIn !== null && openedIn !== phase) setOpenedIn(null);
  const canFold = layoutOf(phase).mapFolded && !mapWanted;
  const folded = canFold && openedIn !== phase;
  const toggle = useCallback(() => setOpenedIn((was) => (was === phase ? null : phase)), [phase]);
  return { canFold, folded, toggle };
}
